import { useEffect, useId, useRef, type MouseEvent, type ReactNode } from 'react';
import { cx, sizeClass, type Hue } from './shared';

export interface ModalProps {
  open: boolean;
  /** Called on Escape, the close button, or a click on the backdrop (never when dismissible is false). */
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Buttons for the footer. */
  footer?: ReactNode;
  size?: 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Accent line along the top edge. */
  hue?: Hue;
  /** Don't close on a backdrop click (e.g. unsaved form). Escape still closes. */
  persistent?: boolean;
  /**
   * false: opened with show() instead of showModal(). The page stays usable: no backdrop,
   * no focus trap, no scroll lock (the consent banner). Default true.
   */
  blocking?: boolean;
  /**
   * false: only the modal's own buttons close it. No close button, and Escape and the
   * backdrop do nothing (the consent banner). Default true.
   */
  dismissible?: boolean;
  /** Pin it to the bottom or top edge instead of centring it. */
  dock?: 'bottom' | 'top';
  /** Without a title the header is left out; name it with aria-label instead. */
  'aria-label'?: string;
  className?: string;
  /** Extra classes on .modal__body, e.g. p-0 for a full-bleed image. */
  bodyClassName?: string;
}

/** A native <dialog> opened with showModal(): top layer, focus trap and Escape come built in. */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size,
  hue,
  persistent,
  blocking = true,
  dismissible = true,
  dock,
  'aria-label': ariaLabel,
  className,
  bodyClassName,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (blocking) dialog.showModal();
      else dialog.show();
    }
    if (!open && dialog.open) dialog.close();
  }, [open, blocking]);

  // Unmounting while open (e.g. navigating away) still releases the page.
  useEffect(() => () => void (ref.current?.open && ref.current.close()), []);

  // A click that STARTED on the backdrop. Dragging to select text in a field
  // and releasing over the backdrop fires a click on the dialog itself (the
  // common ancestor of mousedown and mouseup), which must not close it and
  // throw away what was being typed. (From games' Modal.)
  const downOnBackdrop = useRef(false);

  // A modal mustn't act on the TAIL of the gesture that opened it: when a click
  // opens it, the second click of a double-click lands on the fresh modal's
  // content. The first click a modal sees with detail > 1 belongs to that
  // earlier gesture, so it is swallowed before any child sees it. (From games' Modal.)
  const firstClickSeen = useRef(false);
  useEffect(() => {
    if (open) firstClickSeen.current = false;
  }, [open]);
  const swallowOpeningClick = (e: MouseEvent) => {
    const first = !firstClickSeen.current;
    firstClickSeen.current = true;
    if (first && e.detail > 1) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <dialog
      ref={ref}
      className={cx('modal', sizeClass('modal', size), hue && `modal--${hue}`, dock && `modal--dock-${dock}`, className)}
      aria-labelledby={title ? titleId : undefined}
      aria-label={ariaLabel}
      onCancel={(e) => {
        e.preventDefault(); // React owns the open state
        if (dismissible) onClose();
      }}
      // A click on the dialog element itself (not its contents) is a backdrop
      // click, but only when the press started there too.
      onMouseDown={(e) => (downOnBackdrop.current = e.target === e.currentTarget)}
      onClickCapture={swallowOpeningClick}
      onClick={(e) => {
        const fromBackdrop = downOnBackdrop.current;
        downOnBackdrop.current = false;
        if (dismissible && !persistent && fromBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      {(title || dismissible) && (
        <header className="modal__header">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          {dismissible && (
            <button type="button" className="btn btn--ghost btn--slate btn--icon btn--sm" aria-label="Close" onClick={onClose}>
              <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
                <path d="M6.4 5 12 10.6 17.6 5 19 6.4 13.4 12 19 17.6 17.6 19 12 13.4 6.4 19 5 17.6 10.6 12 5 6.4z" />
              </svg>
            </button>
          )}
        </header>
      )}
      <div className={cx('modal__body', bodyClassName)}>{children}</div>
      {footer && <footer className="modal__footer">{footer}</footer>}
    </dialog>
  );
}
